import { UserOutlined } from '@ant-design/icons';
import { Avatar, message } from 'antd';
import React, { useState } from 'react';
import { AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineEdit, AiOutlineUnlock } from 'react-icons/ai';
import ActionsMenu, { emptyOptions } from '../../../components/base/data-display/actions-menu';
import DataDisplay from '../../../components/base/data-display/data-display-selector';
import { JobAccessListModalForm } from '../../../components/job-access-list-modal-form';
import { Role } from '../../../enums';
import authService from '../../../services/auth.service';
import teamMembersService from '../../../services/team-members.service';
import { IMenuAction } from '../../../types';
import { camelCaseToWord, snakeCaseToWord } from '../../../utils/strings';

const teamMemberItemFactory =
	() =>
	({ item }) => {
		return (
			item && (
				<>
					<div
						className="d-flex flex-column justify-content-center align-items-center"
						style={{ width: '36%' }}
					>
						<Avatar shape="square" size={40} icon={<UserOutlined />} className="mb-2" />
						<span style={{ fontWeight: 500, textAlign: 'center' }}>
							{item.profile.firstName} {item.profile.lastName}
						</span>
					</div>

					<div
						className="w-100 d-flex flex-column align-items-center justify-content-center"
						style={{ marginLeft: '-8%' }}
					>
						<span>{item.email}</span>
						<span>{item.profile.title}</span>
						<span>{item.role}</span>
						<span>
							Created at:
							{
								<span className="ps-3">
									{item.profile.createdAt
										? new Date(item.profile.createdAt).toLocaleDateString()
										: 'NA'}
								</span>
							}
						</span>
						<span>
							Last login:
							{
								<span className="ps-3">
									{item.lastLogin ? new Date(item.lastLogin).toLocaleDateString() : 'NA'}
								</span>
							}
						</span>
					</div>
				</>
			)
		);
	};

const TeamMembersList: React.FC<any> = ({
	data,
	loading,
	onChangeActiveState,
	onChangeRole,
	controlledAccessEnabled
}: any) => {
	const [controlledAccessListModalVisible, setControlledAccessListModalVisible] = useState<any>([]);
	const [selectedUser, setSelectedUser] = useState<any>();

	const tableColumns = [
		{
			title: (
				<span className="d-inline-block ps-3 py-1" style={{ color: '#8DABC4' }}>
					Name
				</span>
			),
			dataIndex: 'name',
			key: 'name',
			render: (text, record) => (
				<div className="d-flex align-items-center">
					<Avatar shape="square" size={40} icon={<UserOutlined />} src={record.profile.avatar} />
					<div className="ps-3 d-flex flex-column">
						{record.profile.firstName && record.profile.lastName ? (
							<span style={{ fontWeight: 500 }}>
								{record.profile.firstName} {record.profile.lastName}
							</span>
						) : null}
						<span>{record.email}</span>
					</div>
				</div>
			)
		},
		{
			title: (
				<span className="d-inline-block ps-3 py-1" style={{ color: '#8DABC4' }}>
					Title
				</span>
			),
			dataIndex: 'profile.title',
			key: 'profile.title',
			render: (text, record) => <span className="ps-3">{text || 'NA'}</span>
		},
		{
			title: (
				<span className="d-inline-block ps-3 py-1" style={{ color: '#8DABC4' }}>
					Role
				</span>
			),
			dataIndex: 'role',
			key: 'role',
			render: (text, record) => <span className="ps-3">{text}</span>
		},
		{
			title: (
				<span className="d-inline-block ps-3 py-1" style={{ color: '#8DABC4' }}>
					Created At
				</span>
			),
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (text, record) => (
				<span className="ps-3">
					{record.profile.createdAt ? new Date(record.profile.createdAt).toLocaleDateString() : 'NA'}
				</span>
			)
		},
		{
			title: (
				<span className="d-inline-block ps-3 py-1" style={{ color: '#8DABC4' }}>
					Last Login
				</span>
			),
			dataIndex: 'lastLogin',
			key: 'lastLogin',
			render: (text, record) => <span className="ps-3">{text ? new Date(text).toLocaleDateString() : 'NA'}</span>
		},
		{
			title: 'Actions',
			dataIndex: 'actions',
			key: 'actions',
			render: (text, record) => (
				<ActionsMenu
					whenEmpty={emptyOptions.DISABLED}
					dotsOrientation="horizontal"
					options={actionsFactory(record)}
				/>
			)
		}
	];

	const changeMemberActiveStateHandler = async (member, activeState) => {
		try {
			const response = await teamMembersService.changeTeamMemberActiveState(member, activeState);
			onChangeActiveState({ memberId: member }, response);
			message.success(`Successfully ${activeState == 'true' ? 'activated' : 'deactivated'} the team member`);
		} catch (e) {
			message.error("couldn't change the team member active state");
		}
	};

	const changeTeamMemberRole = async (member, role) => {
		try {
			const response = await teamMembersService.changeTeamMemberRole(member, role);
			onChangeRole({ memberId: member }, response);
			message.success(`Successfully changed the team member's role to ${snakeCaseToWord(role)}`);
		} catch (e) {
			message.error("couldn't change the team member role");
		}
	};
	const actionsMenuGuard = memberId => {
		const userId = authService.getUserData().userId;
		return authService.hasRoles(Role.COMPANY_OWNER, Role.COMPANY_ADMIN) && userId !== memberId;
	};

	const actionsFactory = record => {
		const previligedUser = authService.hasRoles(Role.COMPANY_OWNER, Role.COMPANY_ADMIN);

		const activeState = record.activeMember ? 'deactivateMember' : 'activateMember';
		const actions: IMenuAction[] = [];
		if (record.role !== 'Owner' && previligedUser) {
			actions.push({
				name: camelCaseToWord(activeState),
				key: activeState,
				icon: record.activeMember ? <AiOutlineCloseCircle /> : <AiOutlineCheckCircle />,
				guard: () => actionsMenuGuard(record.id),
				action: () => changeMemberActiveStateHandler(record.id, record.activeMember ? 'false' : 'true')
			});
			actions.push({
				name: `Change to ${record.role === 'Admin' ? 'user' : 'admin'}`,
				key: record.role,
				icon: <AiOutlineEdit />,
				guard: () => actionsMenuGuard(record.id),
				action: () =>
					changeTeamMemberRole(record.id, record.role === 'Admin' ? Role.COMPANY_USER : Role.COMPANY_ADMIN)
			});
		}
		if (controlledAccessEnabled && authService.hasRoles(Role.COMPANY_OWNER) && record.role !== 'Owner') {
			actions.push({
				name: 'Allowed Jobs',
				key: 'allowedJobs',
				action: () => {
					setControlledAccessListModalVisible(true);
					setSelectedUser(record);
				},
				icon: <AiOutlineUnlock />
			});
		}
		return actions;
	};
	return (
		<>
			<DataDisplay
				className="ta-table"
				data={data}
				mobileData={[data]}
				loading={loading}
				tableColumns={tableColumns}
				listItemComponent={teamMemberItemFactory()}
				renderActions={actionsFactory}
				size="small"
				pagination={{ position: 'bottom' }}
			/>
			{authService.hasRoles(Role.COMPANY_OWNER) && selectedUser ? (
				<JobAccessListModalForm
					selectedUser={selectedUser}
					visible={controlledAccessListModalVisible}
					onCancel={() => setControlledAccessListModalVisible(false)}
				/>
			) : null}
		</>
	);
};

export default TeamMembersList;
